import React, { CSSProperties, useCallback, useMemo, useState } from 'react';

import {
  Box,
  ClassNameMap,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

import { useInfiniteScroll } from '../hooks/use-infinite-scroll.hook';
import { Loader } from '../Loader';
import { TableColumn } from '../Table';
import { NoItemsLoadingBody, NoItemsOverlayBody, OverlayProps } from '../Table/Overlay';
import { FlexTableCell, FlexTableHeadCell } from './FlexTableCell.component';
import { FlexTableRow } from './FlexTableRow.component';
import { useMinWidthExceeded } from './use-min-width-exceeded.hook';

type ObjectStringKeys<Obj extends Record<string, unknown>> = Extract<keyof Obj, string>;

export type FlexTableColumn<
  Row extends Record<string, unknown>,
  Field extends ObjectStringKeys<Row> = ObjectStringKeys<Row>,
> = (Pick<TableColumn<Row, Field>, 'renderCell' | 'headerName' | 'sortable' | 'sortComparator' | 'align' | 'cellClassName'> & {
  field: Field;
  sortResettable?: boolean;
  flex?: number;
  minWidth?: CSSProperties['minWidth'];
  maxWidth?: CSSProperties['maxWidth'];
  width?: CSSProperties['width'];
});

export type FlexTableColumnWithId<
  Row extends Record<string, unknown>,
  Field extends ObjectStringKeys<Row> = ObjectStringKeys<Row>
> = FlexTableColumn<Row, Field> & { id: string };

export type PinColumn = 'start' | 'end';
export type PinRow = 'top' | 'bottom';

export type FlexTableSort<ColumnId extends string = string> = {
  colId: ColumnId;
  type: 'asc' | 'desc';
};

export type FlexTableProps<
  Row extends Record<string, unknown>,
  RowField extends ObjectStringKeys<Row> = ObjectStringKeys<Row>
> = {
  columns: FlexTableColumn<Row, RowField>[];
  rows: Row[];
  pinColumn?: PinColumn[] | PinColumn;
  pinRow?: Partial<Record<PinRow, Row[]>>;
  cellClasses?: Partial<ClassNameMap<'head' | 'root'>>;
  renderSearchBar?: (resetPage: () => void) => React.ReactNode;
  infiniteScroll?: {
    perPage?: number;
  };
  getCellClassName?: (row: Row, column: FlexTableColumn<Row, RowField>) => string | undefined;
  headCellHeight?: number;
  cellHeight?: number;
  maxHeight?: string | number;
  emptyOverlayProps?: OverlayProps;
  onRowClick?: (row: Row) => void;
  horizontalScroll?: boolean;
  isLoading?: boolean;
  hidePagination?: boolean;
  rowsPerPageOptions?: number[];
  dense?: boolean;
  rowDetailsContent?: React.ComponentType<{ row: Row }>;
  expandButtonPlacement?: 'start' | 'end';
  initialSort?: FlexTableSort<RowField>;
  filterContent?: React.ReactElement;
  getRowId?: (row: Row) => string;
};

const TABLE_ID = 'flex-table';

const getPinnedIndex = (pin: PinColumn | PinRow, arrayLength: number) => {
  return pin === 'start' || pin === 'top' ? 0 : arrayLength - 1;
};

export const FlexTable = <Row extends Record<string, unknown>>({
  rows,
  columns: _columns,
  pinColumn,
  cellClasses,
  infiniteScroll,
  pinRow,
  getCellClassName,
  cellHeight = 56,
  headCellHeight = cellHeight,
  maxHeight,
  onRowClick,
  horizontalScroll = true,
  emptyOverlayProps = {},
  renderSearchBar,
  isLoading = false,
  hidePagination = false,
  rowsPerPageOptions = [ 10, 20, 30 ],
  dense,
  rowDetailsContent: RowDetailsContentComponent,
  expandButtonPlacement = 'start',
  initialSort,
  filterContent,
  getRowId = (row) => row.key as string,
}: FlexTableProps<Row>) => {
  const columns = useMemo(() => _columns.map((column) => ({ ...column, id: column.field })), [ _columns ]);
  const { elementRef, minWidthExceeded } = useMinWidthExceeded({ disabled: !pinColumn, innerElementId: TABLE_ID });
  const [ page, setPage ] = useState(0);
  const [ rowsPerPage, setRowsPerPage ] = useState(rowsPerPageOptions[0]);
  const [ sort, setSort ] = useState<FlexTableSort | null>(initialSort ?? null);
  const [ collapsedItems, setCollapsed ] = React.useState<Record<string, boolean>>({});
  const { classes, cx } = useStyles();

  const resetPage = useCallback(() => {
    if (!infiniteScroll) {
      setPage(0);
    }
    elementRef.current?.scrollTo(0, 0);
  }, [ elementRef, infiniteScroll ]);

  const { loaderRef, dataAmount, showLoader } = useInfiniteScroll({ rowsLength: rows?.length, perPage: infiniteScroll?.perPage, disabled: !infiniteScroll });

  // little state machine: null -> asc -> desc
  const onSortChangeHandler = useCallback((colId: string) => {
    const columnConfig = columns.find(({ id }) => id === colId);

    setSort((prev) => {
      if (prev === null || prev?.colId !== colId) {
        return { colId, type: 'asc' };
      }
      if (prev.type === 'asc') {
        return { colId, type: 'desc' };
      }
      if (columnConfig?.sortResettable === false) {
        return { colId, type: 'asc' };
      }
      return null;
    });
  }, []);

  const onChangePageHandler = useCallback((_: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage);
  }, []);

  const onChangeRowsPerPageHandler = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find(({ id }) => id === sort.colId);

    if (!column) return rows;

    const getFieldStringValue = (value: any) => {
      if (typeof value === 'object' && value !== null) {
        const firstValue = Object.values(value)[0];

        return firstValue !== undefined ? firstValue : '';
      }
      return value !== undefined ? value : '';
    };

    if (column.sortComparator) {
      return [ ...rows ].sort((a, b) => {
        if (!column.sortComparator) {
          return 0;
        }

        const valueA = a[column.field];
        const valueB = b[column.field];

        const sortVal = column.sortComparator(valueA, valueB, a, b, sort.type);

        return sort.type === 'asc' ? sortVal : sortVal * -1;
      });
    }

    return [ ...rows ].sort((a, b) => {
      const valueA = a[column.field];
      const valueB = b[column.field];

      const v1 = getFieldStringValue(valueA);
      const v2 = getFieldStringValue(valueB);
      const outcome = sort.type === 'asc' ? v1 > v2 : v1 < v2;

      return outcome ? 1 : -1;
    });
  }, [ columns, rows, sort ]);

  const slicedRows = useMemo(() => {
    if (infiniteScroll) {
      return sortedRows?.slice(0, dataAmount);
    }

    if (hidePagination) {
      return sortedRows;
    }

    return sortedRows?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [ dataAmount, infiniteScroll, page, rowsPerPage, sortedRows, hidePagination ]);

  const pinnedColumnIndexes = useMemo(() => !pinColumn ? [] : (Array.isArray(pinColumn) ? pinColumn : [ pinColumn ]).map((pinColumn) => ({ index: getPinnedIndex(pinColumn, columns.length), position: pinColumn })), [ columns.length, pinColumn ]);

  const renderRows = useCallback((rows?: Row[], pinRowPosition?: PinRow) => {
    return rows?.map((row, index) => {
      const rowId = getRowId(row) ?? index;

      const handleCollapseToggle = () => {
        setCollapsed(collapsed => ({
          ...collapsed,
          [rowId]: !collapsed[rowId]
        }));
      };

      const isCollapsed = collapsedItems[rowId];

      return (
        <React.Fragment key={index}>
          <FlexTableRow
            headCellHeight={headCellHeight}
            data-pinrow={pinRowPosition}
            onClick={onRowClick && (() => onRowClick?.(row))}
            testId={`flex-table-row-${rowId}`}
          >
            {RowDetailsContentComponent && expandButtonPlacement === 'start' && (
              <TableCell className={classes.expandCell}>
                <IconButton onClick={handleCollapseToggle}>
                  {!isCollapsed ? <ExpandMore /> : <ExpandLess />}
                </IconButton>
              </TableCell>
            )}
            {columns.map((column, index) => {
              const pinColumn = pinnedColumnIndexes.find(col => col.index === index);

              const value = row[column.field];
              const content = column.renderCell
                ? React.createElement(column.renderCell, { value, row })
                : value;
              const className = getCellClassName?.(row, column);

              return (
                <FlexTableCell
                  key={`${column.id}_${index}`}
                  style={{ width: column.width, minWidth: column.minWidth ?? column.width, maxWidth: column.width }}
                  className={cx(className, String(column.cellClassName))}
                  classes={cellClasses}
                  align={column.align}
                  data-pincolumn={minWidthExceeded ? pinColumn?.position : undefined}
                  data-pinrow={pinRowPosition}
                  height={cellHeight}
                  testId={`${column.id}-cell-row-${rowId}`}
                >
                  {content}
                </FlexTableCell>
              );
            })}
            {RowDetailsContentComponent && expandButtonPlacement === 'end' && (
              <TableCell className={classes.expandCell}>
                <IconButton onClick={handleCollapseToggle}>
                  {!isCollapsed ? <ExpandMore /> : <ExpandLess />}
                </IconButton>
              </TableCell>
            )}
          </FlexTableRow>
          {RowDetailsContentComponent && isCollapsed && (
            <RowDetailsContentComponent row={row} />
          )}
        </React.Fragment>
      );
    });
  }, [ RowDetailsContentComponent, cellClasses, cellHeight, classes.expandCell, collapsedItems, columns, getCellClassName, headCellHeight, minWidthExceeded, onRowClick, pinnedColumnIndexes, expandButtonPlacement ]);

  const tableStyles: React.CSSProperties = React.useMemo(() => {
    if (horizontalScroll) {
      return {
        width: '100%',
      };
    }

    return {
      width: '100%',
      tableLayout: 'fixed',
    };
  }, [ horizontalScroll ]);

  const pagination = useMemo(() => {
    if (hidePagination) {
      return null;
    }

    if (infiniteScroll) {
      return null;
    }

    return (
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={rows?.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePageHandler}
        onRowsPerPageChange={onChangeRowsPerPageHandler}
      />
    );
  }, [ hidePagination, infiniteScroll, onChangePageHandler, onChangeRowsPerPageHandler, page, rows?.length, rowsPerPage, rowsPerPageOptions ]);

  const filters = useMemo(() => {
    if (filterContent) {
      return filterContent;
    }
    return null;
  }, [ filterContent ]);


  const table = useMemo(() => {
    if (isLoading) {
      return <NoItemsLoadingBody />;
    }

    if (!rows?.length) {
      return <NoItemsOverlayBody {...emptyOverlayProps} />;
    }

    return (
      <>
        <TableContainer ref={elementRef} style={{ maxHeight, overflow: 'auto' }}>
          <Table
            id={TABLE_ID}
            style={tableStyles}
            stickyHeader
            size={dense ? 'small' : undefined}
          >
            <TableHead>
              <TableRow>
                {RowDetailsContentComponent && expandButtonPlacement === 'start' && (<TableCell className={classes.expandHeadCell} />)}
                {columns.map((column, index) => {
                  const pin = pinnedColumnIndexes.find(col => col.index === index);

                  return (
                    <FlexTableHeadCell
                      key={`${column.id}_${index}`}
                      style={{ width: column.width, minWidth: column.minWidth }}
                      classes={cellClasses}
                      id={column.id}
                      onSortClick={onSortChangeHandler}
                      columnSort={sort?.colId === column.id ? sort?.type : null}
                      sortable={column.sortable}
                      align={column.align}
                      data-pincolumn={minWidthExceeded ? pin?.position : undefined}
                      height={headCellHeight}
                    >
                      {column.headerName}
                    </FlexTableHeadCell>
                  );
                })}
                {RowDetailsContentComponent && expandButtonPlacement === 'end' && (<TableCell className={classes.expandHeadCell} />)}
              </TableRow>
            </TableHead>
            <TableBody>
              {renderRows(pinRow?.top, 'top')}
              {renderRows(slicedRows)}
              {renderRows(pinRow?.bottom, 'bottom')}
              {showLoader && (
                <TableRow ref={loaderRef}>
                  <TableCell colSpan={columns.length} align="center" height={cellHeight}>
                    <Loader size="24px" thickness={5} color="primary" />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {pagination}
      </>
    );
  }, [ isLoading, rows?.length, elementRef, maxHeight, tableStyles, dense, RowDetailsContentComponent, columns, renderRows, pinRow?.top, pinRow?.bottom, slicedRows, showLoader, loaderRef, cellHeight, pagination, emptyOverlayProps, pinnedColumnIndexes, cellClasses, onSortChangeHandler, sort?.colId, sort?.type, minWidthExceeded, headCellHeight, expandButtonPlacement ]);

  return (
    <>
      <Box className={cx(classes.searchBox, filters ? classes.withFilters : null)}>
        {renderSearchBar?.(resetPage)}
        {filters}
      </Box>
      {filters && (
        <Box>
          <Divider />
        </Box>
      )}
      {table}
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  expandHeadCell: {
    backgroundColor: '#fff',
    width: '48px',
  },
  expandCell: {
    padding: 0,
  },
  searchBox: {
    display: 'flex',
    flexDirection: 'row'
  },
  withFilters: {
    borderTop: `1px solid ${theme.palette.grey[300]}`
  }
}));
